import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Editor } from "@tinymce/tinymce-react";

const ChapterEdit = ({ isNewChapter = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState({
    title: "",
    content: "",
    image_path: "",
    action1_text: "",
    action1_next_chapter: null,
    action2_text: "",
    action2_next_chapter: null,
    clues: [],
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [chapters, setChapters] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Only fetch chapter data if we're editing an existing chapter
        if (!isNewChapter && id) {
          const response = await axios.get(`/api/chapters/${id}`);
          const data = response.data;
          // Parse clues from JSON string if it's a string
          const clues =
            typeof data.clues === "string"
              ? JSON.parse(data.clues)
              : Array.isArray(data.clues)
              ? data.clues
              : [];
          setChapter({ ...data, clues });
        }

        // Fetch all chapters for the dropdowns
        const chaptersResponse = await axios.get("/api/chapters");
        setChapters(chaptersResponse.data);
      } catch (err) {
        setError("Error loading data");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isNewChapter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      if (isNewChapter) {
        await axios.post("/api/chapters", chapter);
        navigate("/");
      } else {
        await axios.put(`/api/chapters/${id}`, chapter);
        setSuccess("Chapter updated successfully");
        setTimeout(() => navigate(`/chapter/${id}`), 2000);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
      } else {
        console.error("Error saving chapter:", err);
        setError("Error saving chapter");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      setSubmitting(true);
      const response = await axios.post("/api/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Store just the filename, not the full path
      const filename = response.data.path.split("/").pop();
      setChapter({ ...chapter, image_path: filename });
    } catch (err) {
      console.error("Error uploading image:", err);
      setError("Error uploading image");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClueChange = (index, field, value) => {
    const newClues = [...chapter.clues];
    if (!newClues[index]) {
      newClues[index] = { question: "", answer: "" };
    }
    newClues[index][field] = value;
    setChapter({ ...chapter, clues: newClues });
  };

  const addClue = () => {
    setChapter({
      ...chapter,
      clues: [...chapter.clues, { question: "", answer: "" }],
    });
  };

  const removeClue = (index) => {
    const newClues = chapter.clues.filter((_, i) => i !== index);
    setChapter({ ...chapter, clues: newClues });
  };

  const handleRemoveImage = async () => {
    try {
      setSubmitting(true);
      const response = await axios.put(`/api/chapters/${id}`, {
        ...chapter,
        image_path: null, // Set image_path to null to remove it
      });

      if (response.data.message === "Chapter updated successfully") {
        setChapter((prev) => ({ ...prev, image_path: "" }));
      }
    } catch (err) {
      console.error("Error removing image:", err);
      setError("Error removing image");
    } finally {
      setSubmitting(false);
    }
  };

  // If not authenticated, don't render the form
  if (!localStorage.getItem("token")) {
    return null;
  }

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen bg-sacred-white">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="text-earth-red text-center p-4 bg-sacred-white">
        Error: {error}
      </div>
    );

  return (
    <div className="min-h-screen bg-sacred-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-semibold text-obsidian-black mb-4">
            {isNewChapter ? "Create New Chapter" : "Edit Chapter"}
          </h1>
        </div>

        {success && <div className="text-green-500 mb-4">{success}</div>}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="title"
              className="block text-sm font-medium text-obsidian-black mb-1"
            >
              Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={chapter.title}
              onChange={(e) =>
                setChapter({ ...chapter, title: e.target.value })
              }
              required
              className="w-full px-4 py-2 border border-sage-green rounded-md focus:outline-none focus:ring-2 focus:ring-turquoise-green focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="content"
              className="block text-sm font-medium text-obsidian-black mb-1"
            >
              Content
            </label>
            <Editor
              value={chapter.content}
              apiKey="jgiietlifrjseztk3mfsb2aoid6smcny5yehbmkoms2279kh"
              onEditorChange={(content) => setChapter({ ...chapter, content })}
              init={{
                height: 500,
                menubar: true,
                plugins: [
                  "anchor",
                  "autolink",
                  "charmap",
                  "codesample",
                  "emoticons",
                  "image",
                  "link",
                  "lists",
                  "media",
                  "searchreplace",
                  "table",
                  "visualblocks",
                  "wordcount",
                ],
                toolbar:
                  "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table mergetags | addcomment showcomments | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat",
                content_style:
                  'body { font-family: "National Park", sans-serif; font-weight: 300; }',
              }}
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Image</label>
            <input
              type="file"
              onChange={handleFileUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {chapter.image_path && (
              <div className="mt-4">
                <p className="text-sm mb-2">
                  Current Image: {chapter.image_path}
                </p>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={submitting}
                  className="text-red-600 hover:text-red-800 font-medium"
                >
                  Remove Image
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2">Action 1 Text</label>
            <input
              type="text"
              value={chapter.action1_text}
              onChange={(e) =>
                setChapter({ ...chapter, action1_text: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Action 1 Next Chapter</label>
            <select
              value={chapter.action1_next_chapter || ""}
              onChange={(e) =>
                setChapter({
                  ...chapter,
                  action1_next_chapter: e.target.value
                    ? parseInt(e.target.value)
                    : null,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">None</option>
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-2">Action 2 Text</label>
            <input
              type="text"
              value={chapter.action2_text}
              onChange={(e) =>
                setChapter({ ...chapter, action2_text: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Action 2 Next Chapter</label>
            <select
              value={chapter.action2_next_chapter || ""}
              onChange={(e) =>
                setChapter({
                  ...chapter,
                  action2_next_chapter: e.target.value
                    ? parseInt(e.target.value)
                    : null,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">None</option>
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-2">Clues</label>
            {chapter.clues.map((clue, index) => (
              <div key={index} className="mb-4 p-4 border rounded-lg">
                <div className="mb-4">
                  <label className="block text-sm mb-2">
                    Question {index + 1}
                  </label>
                  <input
                    type="text"
                    value={clue.question}
                    onChange={(e) =>
                      handleClueChange(index, "question", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">
                    Answer {index + 1}
                  </label>
                  <input
                    type="text"
                    value={clue.answer}
                    onChange={(e) =>
                      handleClueChange(index, "answer", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeClue(index)}
                  className="mt-2 text-red-600 hover:text-red-800 font-medium"
                >
                  Remove Clue
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addClue}
              className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              Add Clue
            </button>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-sacred-white bg-turquoise-green hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-turquoise-green disabled:opacity-50"
            >
              {submitting ? "Saving..." : "Save Chapter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChapterEdit;
